<script lang="ts">
  import StatusBadge from '../../framework/components/app/StatusBadge.svelte';
  import { BUDGET_OPTIONS, TERRITORIES, buildSteps } from './questions';
  import { isStepAnswered, stepPreview } from './answers';
  import type { BriefBuilderAnswers } from './types';

  export let answers: BriefBuilderAnswers;
  /** Optional scores aligned to builder step order (q1…); sparse ok. */
  export let stepScores: Array<{ score?: number | null; feedback?: string | null }> = [];

  $: steps = buildSteps(answers);

  function scoreTone(score: number): 'success' | 'info' | 'warn' | 'danger' {
    if (score >= 76) return 'success';
    if (score >= 51) return 'info';
    if (score >= 26) return 'warn';
    return 'danger';
  }

  function formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(navigator.language, { dateStyle: 'medium' });
  }
</script>

<div class="bb-answers">
  {#each steps as step, index (step.id)}
    {@const done = isStepAnswered(step, answers)}
    {@const unknown = answers.unknowns.includes(step.id)}
    {@const scored = stepScores[index]}
    {@const scoreable = step.kind === 'text'}
    {@const score =
      scoreable && scored?.score !== null && scored?.score !== undefined
        ? Number(scored.score)
        : null}
    <article class="bb-answer-card" class:empty={!done && !unknown}>
      <header class="bb-answer-head">
        <span class="bb-answer-num" aria-hidden="true">{step.number}</span>
        <div class="bb-answer-titles">
          <span class="bb-answer-label">{step.label}</span>
          <h3 class="bb-answer-prompt">{step.prompt}</h3>
        </div>
        {#if score !== null && !Number.isNaN(score)}
          <span class="bb-answer-score tone-{scoreTone(score)}">{score}%</span>
        {/if}
      </header>

      <div class="bb-answer-body">
        {#if unknown}
          <StatusBadge label="Marked unknown" tone="warn" />
          <p class="bb-answer-note">Research will find this out.</p>
        {:else if step.kind === 'territory'}
          <div class="bb-option-grid" role="list" aria-label="Selected territory">
            {#each TERRITORIES as t (t.id)}
              <div
                class="bb-option-card"
                class:selected={answers.territory === t.id}
                role="listitem"
                aria-current={answers.territory === t.id ? 'true' : undefined}
              >
                <span class="bb-option-title">{t.title}</span>
                <span class="bb-option-desc">{t.description}</span>
                {#if answers.territory === t.id}
                  <span class="bb-option-check" aria-hidden="true">
                    <i class="check icon"></i>
                  </span>
                {/if}
              </div>
            {/each}
          </div>
        {:else if step.kind === 'practicalities'}
          <div class="bb-pract-blocks">
            <section class="bb-pract-block" aria-label="Budget">
              <h4 class="bb-pract-legend">Budget feel</h4>
              <div class="bb-chip-row" role="list">
                {#each BUDGET_OPTIONS as opt (opt.id)}
                  <span
                    class="bb-chip"
                    class:selected={answers.practicalities.budget === opt.id &&
                      !answers.practicalities.budgetUnknown}
                    role="listitem"
                  >
                    {opt.label}
                  </span>
                {/each}
                <span
                  class="bb-chip"
                  class:selected={answers.practicalities.budgetUnknown}
                  role="listitem"
                >
                  No idea yet
                </span>
              </div>
            </section>

            <section class="bb-pract-block" aria-label="Decision date">
              <h4 class="bb-pract-legend">Decision date</h4>
              {#if answers.practicalities.noFixedDate}
                <span class="bb-chip selected">No fixed date</span>
              {:else if answers.practicalities.decisionDate}
                <span class="bb-chip selected date">
                  <i class="calendar outline icon" aria-hidden="true"></i>
                  {formatDate(answers.practicalities.decisionDate)}
                </span>
              {:else}
                <span class="bb-muted">Not set</span>
              {/if}
            </section>

            <section class="bb-pract-block" aria-label="Geography">
              <h4 class="bb-pract-legend">Geography</h4>
              {#if answers.practicalities.geography?.trim()}
                <p class="bb-geo">{answers.practicalities.geography.trim()}</p>
              {:else}
                <span class="bb-muted">Not set</span>
              {/if}
              {#if answers.practicalities.crossBorders}
                <span class="bb-chip selected meta">Crosses borders in ways that matter</span>
              {/if}
            </section>

            <section class="bb-pract-block" aria-label="Result solidity">
              <h4 class="bb-pract-legend">How solid do the results need to be?</h4>
              <div class="bb-solidity-row">
                <div
                  class="bb-solidity"
                  class:selected={answers.practicalities.solidity === 'directional'}
                >
                  <strong>Directional</strong>
                  <span>Enough to shape thinking and next steps.</span>
                </div>
                <div
                  class="bb-solidity"
                  class:selected={answers.practicalities.solidity === 'board_grade'}
                >
                  <strong>Board-grade</strong>
                  <span>Strong enough to defend to a board or investor.</span>
                </div>
              </div>
            </section>
          </div>
        {:else}
          <p class="bb-answer-text">
            {stepPreview(step, answers) || 'No answer provided'}
          </p>
        {/if}

        {#if scoreable && scored?.feedback}
          <div class="bb-feedback">
            <i class="lightbulb outline icon" aria-hidden="true"></i>
            <p>{scored.feedback}</p>
          </div>
        {/if}
      </div>
    </article>
  {/each}
</div>

<style>
  .bb-answers {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
  }

  .bb-answer-card {
    background: var(--white);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-card);
    padding: var(--space-5);
    box-shadow: var(--shadow-card);
  }

  .bb-answer-card.empty {
    opacity: 0.72;
  }

  .bb-answer-head {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-4);
    padding-bottom: var(--space-3);
    border-bottom: 1px solid var(--border-color);
  }

  .bb-answer-num {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.25rem;
    height: 2.25rem;
    padding: 0 0.4rem;
    border-radius: var(--radius-input);
    background: var(--primary);
    color: var(--white);
    font-size: var(--text-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .bb-answer-titles {
    flex: 1;
    min-width: 0;
  }

  .bb-answer-label {
    display: block;
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--accent-orange-ink);
    margin-bottom: 0.2rem;
  }

  .bb-answer-prompt {
    margin: 0;
    font-size: var(--text-base);
    font-weight: 500;
    color: var(--grey);
    line-height: 1.4;
    text-wrap: pretty;
  }

  .bb-answer-score {
    flex-shrink: 0;
    font-size: var(--text-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    padding: 0.2rem 0.55rem;
    border-radius: var(--radius-pill);
  }

  .bb-answer-score.tone-success {
    background: var(--status-success-soft);
    color: var(--status-success-ink);
  }
  .bb-answer-score.tone-info {
    background: var(--status-info-soft);
    color: var(--status-info-ink);
  }
  .bb-answer-score.tone-warn {
    background: var(--status-warn-soft);
    color: var(--status-warn-ink);
  }
  .bb-answer-score.tone-danger {
    background: var(--status-danger-soft);
    color: var(--status-danger-ink);
  }

  .bb-answer-text {
    margin: 0;
    font-size: var(--text-base);
    color: var(--grey);
    line-height: 1.55;
    white-space: pre-wrap;
  }

  .bb-answer-note {
    margin: var(--space-2) 0 0;
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .bb-option-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
    gap: var(--space-2);
  }

  .bb-option-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: var(--space-3);
    border-radius: var(--radius-input);
    border: 1px solid var(--border-color);
    background: var(--greyish);
    opacity: 0.55;
  }

  .bb-option-card.selected {
    opacity: 1;
    background: var(--white);
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .bb-option-title {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
  }

  .bb-option-desc {
    font-size: var(--text-xs);
    color: var(--grey-secondary);
    line-height: 1.35;
  }

  .bb-option-check {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    color: var(--primary);
  }

  .bb-option-check :global(i.icon) {
    margin: 0;
  }

  .bb-pract-blocks {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .bb-pract-legend {
    margin: 0 0 var(--space-2);
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--grey-secondary);
  }

  .bb-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .bb-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.75rem;
    border-radius: var(--radius-pill);
    border: 1px solid var(--border-color);
    background: var(--greyish);
    color: var(--grey-secondary);
    font-size: var(--text-sm);
    opacity: 0.7;
  }

  .bb-chip.selected {
    opacity: 1;
    background: var(--accent-orange-soft);
    border-color: var(--accent-orange);
    color: var(--accent-orange-ink);
    font-weight: 500;
  }

  .bb-chip.date :global(i.icon) {
    margin: 0;
    font-size: 0.9em;
  }

  .bb-chip.meta {
    margin-top: var(--space-2);
  }

  .bb-geo {
    margin: 0 0 var(--space-2);
    font-size: var(--text-base);
    color: var(--grey);
    line-height: 1.45;
  }

  .bb-muted {
    font-size: var(--text-sm);
    color: var(--grey-secondary);
  }

  .bb-solidity-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: var(--space-2);
  }

  .bb-solidity {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-input);
    border: 1px solid var(--border-color);
    background: var(--greyish);
    opacity: 0.55;
  }

  .bb-solidity.selected {
    opacity: 1;
    background: var(--white);
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .bb-solidity strong {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--grey);
  }

  .bb-solidity span {
    font-size: var(--text-xs);
    color: var(--grey-secondary);
    line-height: 1.4;
  }

  .bb-feedback {
    display: flex;
    gap: var(--space-2);
    align-items: flex-start;
    margin-top: var(--space-4);
    padding: var(--space-3);
    border-radius: var(--radius-input);
    background: var(--status-info-soft);
    color: var(--status-info-ink);
  }

  .bb-feedback :global(i.icon) {
    margin: 0.1rem 0 0;
  }

  .bb-feedback p {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.45;
  }

  @media (max-width: 768px) {
    .bb-answers {
      padding: var(--space-4);
    }

    .bb-answer-card {
      padding: var(--space-4);
    }
  }
</style>
